import type { Plan, EntitlementConfiguration } from "client/api-v2";
import { LifecycleType, ProductPlatform, Tier } from "client/api-v2";
import type { TrialEntitlementsResponse } from "../services/subscriptionService";
import type { PlanWithConfigurableInclusions, PlanInclusionConfiguration } from "../types/plan-extensions";

/**
 * Tiers a subscription can be newly put onto a paid plan for. Excludes Free, which is never billed, and
 * Starter, which is no longer sold — neither should be offered when picking a plan, even though existing
 * subscriptions may still sit on them.
 */
export const billableTiers: Tier[] = [Tier.Professional, Tier.Enterprise, Tier.EnterpriseUnlimited];

export function isPlanInclusionConfigurable(
  plan: Plan | PlanWithConfigurableInclusions | undefined,
  inclusionKey: string
): boolean {
  // Default to non-configurable if no plan provided
  if (!plan) {
    return false;
  }

  // Check if plan has configurableInclusions property (type guard)
  if (!("configurableInclusions" in plan) || !plan.configurableInclusions) {
    return false;
  }

  const config = plan.configurableInclusions[inclusionKey];
  return config?.isConfigurable === true;
}

export function isPlanConfigurable(plan: Plan | PlanWithConfigurableInclusions | undefined): boolean {
  if (!plan) {
    return false;
  }

  return "configurableInclusions" in plan && !!plan.configurableInclusions;
}

export function getPlanInclusionDisplayName(
  plan: Plan | PlanWithConfigurableInclusions | undefined,
  inclusionKey: string,
  fallbackName: string
): string {
  if (!plan) {
    return fallbackName;
  }

  if (!("configurableInclusions" in plan) || !plan.configurableInclusions) {
    return fallbackName;
  }

  const config = plan.configurableInclusions[inclusionKey];
  return config?.displayName || fallbackName;
}

export function getPlanInclusionConfiguration(
  plan: Plan | PlanWithConfigurableInclusions | undefined,
  inclusionKey: string
): PlanInclusionConfiguration | undefined {
  if (!plan) {
    return undefined;
  }

  if (!("configurableInclusions" in plan) || !plan.configurableInclusions) {
    return undefined;
  }

  return plan.configurableInclusions[inclusionKey];
}

export function getPlanInclusionType(
  plan: Plan | PlanWithConfigurableInclusions | undefined,
  inclusionKey: string,
  value: unknown
): "boolean" | "int" | "intOrUnlimited" | "intWithOverrun" | undefined {
  // First check if there's a configuration that specifies the type
  const config = getPlanInclusionConfiguration(plan, inclusionKey);
  if (config?.type) {
    return config.type;
  }

  // Otherwise, infer from the value
  if (typeof value === "boolean") {
    return "boolean";
  }

  if (typeof value === "number") {
    return "int";
  }

  if (typeof value === "object" && value !== null) {
    if ("isUnlimited" in value) {
      if ("overrun" in value || "countDisabledUnits" in value) {
        return "intWithOverrun";
      }
      return "intOrUnlimited";
    }
  }

  return undefined;
}

export function getAllPlanInclusions(plan: Plan | undefined): string[] {
  if (!plan || !plan.planInclusions) {
    return [];
  }

  const inclusions: string[] = [];
  const planInclusions = plan.planInclusions;

  // Get all keys from plan inclusions that have non-null values
  const entries = Object.entries(planInclusions);
  for (const [key, value] of entries) {
    if (value !== null && value !== undefined) {
      inclusions.push(key);
    }
  }

  return inclusions;
}

export function formatInclusionName(key: string): string {
  // Convert camelCase to Title Case
  const result = key.replace(/([A-Z])/g, " $1");
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);

  // Special cases
  const specialCases: Record<string, string> = {
    storageLimitGb: "Storage Limit (GB)",
    taskCap: "Task Cap",
    retentionDays: "Retention Days",
    serviceNow: "ServiceNow Integration",
    jiraServiceManagement: "Jira Service Management",
    spaceLevelInsights: "Space Level Insights",
    auditLogFeeds: "Audit Log Feeds",
    globalDeploymentFreeze: "Global Deployment Freeze",
    processTemplates: "Process Templates",
    projectTemplates: "Project Templates",
    restrictedPermissions: "Restricted Permissions",
    excessAllowedWithUsageDeclaration: "Usage Declaration Excess Allowed",
  };

  return specialCases[key] || finalResult.trim();
}

/**
 * Formats an entitlement value to a human-readable string.
 * Handles IntOrUnlimited, IntOrUnlimitedWithCountDisabledUnits, boolean, number, and null/undefined values.
 * @param value The entitlement value to format
 * @returns A formatted string representation of the value
 */
export function formatEntitlementValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "Not set";
  }

  if (typeof value === "boolean") {
    return value ? "Enabled" : "Disabled";
  }

  if (typeof value === "number") {
    return value.toString();
  }

  if (typeof value === "object" && value !== null && "isUnlimited" in value) {
    if (value.isUnlimited === true) return "Unlimited";
    if ("limit" in value && typeof value.limit === "number") {
      return value.limit.toString();
    }
  }

  return "Not set";
}

export function getInclusionValue(planInclusions: EntitlementConfiguration | undefined, key: string): unknown {
  if (!planInclusions) {
    return undefined;
  }

  // Use Object.entries to safely access the property
  const entries = Object.entries(planInclusions);
  const found = entries.find(([k]) => k === key);
  return found ? found[1] : undefined;
}

export function applyTrialOverrides(
  plan: Plan,
  platform: ProductPlatform,
  trialOverrides: TrialEntitlementsResponse
): Plan {
  const overrides = platform === ProductPlatform.OctopusCloud ? trialOverrides.cloud : trialOverrides.server;

  return {
    ...plan,
    planInclusions: overrides,
  };
}

/**
 * Determines whether a lifecycle type supports custom entitlement overrides.
 * Complimentary, Trial, and Paid support overrides for all tiers; Evergreen accepts connection agents
 * only, so which fields it can be offered comes from the subscription's applicableEntitlementOverrideKeys
 * rather than from this check.
 * @param lifecycleType The lifecycle type to check
 */
export function lifecycleSupportsEntitlementOverrides(lifecycleType: string | undefined): boolean {
  if (!lifecycleType) {
    return false;
  }
  return (
    lifecycleType === LifecycleType.Complimentary ||
    lifecycleType === LifecycleType.Trial ||
    lifecycleType === LifecycleType.Paid ||
    lifecycleType === LifecycleType.Evergreen
  );
}

/**
 * Determines whether a staff task cap change should be mirrored into the subscription's entitlement
 * overrides. Evergreen is excluded: it rejects a task cap override outright, so mirroring one would fail
 * the whole task cap change.
 * @param lifecycleType The lifecycle type to check
 */
export function lifecycleSyncsTaskCapOverride(lifecycleType: string | undefined): boolean {
  return lifecycleSupportsEntitlementOverrides(lifecycleType) && lifecycleType !== LifecycleType.Evergreen;
}
